import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { OrgWidgetData, OrgWidgetEntity } from '../../models/org-widget.models';
import { OrgDialogService } from '../../../org-board/services/org-dialog.service';
import { OrgEntityColorsService } from '../../services/org-entity-colors.service';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { take } from 'rxjs/operators';

/** Processed user row for the directory list. */
interface DirectoryUser {
    key: string;
    name: string;
    email: string;
    photoThumbnail: string;
    initials: string;
    avatarColor: string;
    isOnline: boolean;
    hats: string[];
    positions: string[];
    securityGroups: string[];
    features: string[];
}

/** Deterministic avatar colors based on name hash */
const AVATAR_COLORS = [
    '#e53935',
    '#d81b60',
    '#8e24aa',
    '#5e35b1',
    '#3949ab',
    '#1e88e5',
    '#039be5',
    '#00acc1',
    '#00897b',
    '#43a047',
    '#7cb342',
    '#c0ca33',
    '#f4511e',
    '#6d4c41',
    '#546e7a',
    '#fb8c00',
];

@Component({
    selector: 'shared-user-directory-widget',
    imports: [CommonModule, MatIconModule],
    template: `
        <!-- Search -->
        <div class="search-bar">
            <mat-icon class="search-icon">search</mat-icon>
            <input
                type="text"
                class="search-input"
                placeholder="Search staff…"
                [value]="searchTerm"
                (input)="onSearch($event)"
            />
            <span class="user-count">{{ filteredUsers.length }}</span>
        </div>

        <!-- User List -->
        <div class="user-list" *ngIf="filteredUsers.length; else emptyState">
            <div
                class="user-row"
                *ngFor="let user of filteredUsers; trackBy: trackByKey"
                [class.expanded]="selectedKey === user.key"
                (click)="toggleUser(user.key)"
            >
                <!-- Avatar + name row -->
                <div class="user-summary">
                    <div class="avatar-wrapper clickable" (click)="onAvatarClick($event, user)">
                        <div
                            class="avatar"
                            [class.has-photo]="user.photoThumbnail"
                            [style.background]="!user.photoThumbnail ? user.avatarColor : null"
                        >
                            <img *ngIf="user.photoThumbnail" [src]="user.photoThumbnail" alt="" />
                            <span *ngIf="!user.photoThumbnail" class="initials">{{ user.initials }}</span>
                        </div>
                        <span
                            class="presence-dot"
                            [class.online]="user.isOnline"
                            [title]="user.isOnline ? 'Online' : 'Offline'"
                        ></span>
                    </div>
                    <div class="user-identity">
                        <span class="user-name">{{ user.name }}</span>
                        <span class="user-email" *ngIf="user.email">{{ user.email }}</span>
                    </div>
                    <div class="user-actions">
                        <button class="action-btn" title="View details" (click)="onViewUser($event, user)">
                            <mat-icon>open_in_new</mat-icon>
                        </button>
                        <button class="action-btn" title="Send email" (click)="onEmailUser($event, user)">
                            <mat-icon>email</mat-icon>
                        </button>
                        <button class="action-btn" title="Chat" (click)="onChatUser($event, user)">
                            <mat-icon>chat</mat-icon>
                        </button>
                    </div>
                    <mat-icon class="chevron">
                        {{ selectedKey === user.key ? 'expand_less' : 'expand_more' }}
                    </mat-icon>
                </div>

                <!-- Expanded details -->
                <div class="user-details" *ngIf="selectedKey === user.key">
                    <!-- Hats -->
                    <div class="detail-section" *ngIf="user.hats.length">
                        <div class="detail-header">
                            <mat-icon [style.color]="colorsService.getColor('hats')">school</mat-icon>
                            <span>Hats ({{ user.hats.length }})</span>
                        </div>
                        <div class="chip-list">
                            <span
                                class="chip"
                                *ngFor="let h of user.hats"
                                [style.color]="colorsService.getChipTextColor('hats')"
                                [style.background]="colorsService.getBackground('hats')"
                                >{{ h }}</span
                            >
                        </div>
                    </div>

                    <!-- Positions -->
                    <div class="detail-section" *ngIf="user.positions.length">
                        <div class="detail-header">
                            <mat-icon [style.color]="colorsService.getColor('positions')">account_tree</mat-icon>
                            <span>Org Posts ({{ user.positions.length }})</span>
                        </div>
                        <div class="chip-list">
                            <span
                                class="chip"
                                *ngFor="let p of user.positions"
                                [style.color]="colorsService.getChipTextColor('positions')"
                                [style.background]="colorsService.getBackground('positions')"
                                >{{ p }}</span
                            >
                        </div>
                    </div>

                    <!-- Security Groups -->
                    <div class="detail-section" *ngIf="user.securityGroups.length">
                        <div class="detail-header">
                            <mat-icon [style.color]="colorsService.getColor('securityGroups')">security</mat-icon>
                            <span>Security Groups ({{ user.securityGroups.length }})</span>
                        </div>
                        <div class="chip-list">
                            <span
                                class="chip"
                                *ngFor="let sg of user.securityGroups"
                                [style.color]="colorsService.getChipTextColor('securityGroups')"
                                [style.background]="colorsService.getBackground('securityGroups')"
                                >{{ sg }}</span
                            >
                        </div>
                    </div>

                    <!-- Features -->
                    <div class="detail-section" *ngIf="user.features.length">
                        <div class="detail-header">
                            <mat-icon [style.color]="colorsService.getColor('features')">extension</mat-icon>
                            <span>Features ({{ user.features.length }})</span>
                        </div>
                        <div class="chip-list">
                            <span
                                class="chip"
                                *ngFor="let f of user.features"
                                [style.color]="colorsService.getChipTextColor('features')"
                                [style.background]="colorsService.getBackground('features')"
                                >{{ f }}</span
                            >
                        </div>
                    </div>

                    <!-- No assignments -->
                    <div
                        class="detail-section empty-assignments"
                        *ngIf="
                            !user.hats.length &&
                            !user.positions.length &&
                            !user.securityGroups.length &&
                            !user.features.length
                        "
                    >
                        <mat-icon>info_outline</mat-icon>
                        <span>No hats, positions, or security groups assigned</span>
                    </div>
                </div>
            </div>
        </div>

        <ng-template #emptyState>
            <div class="empty-state">
                <mat-icon>people_outline</mat-icon>
                <span>{{ searchTerm ? 'No matching staff' : 'No staff in organization' }}</span>
            </div>
        </ng-template>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            /* Search */
            .search-bar {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 10px;
                margin-bottom: 8px;
                border-radius: 6px;
                background: #f5f5f5;
                border: 1px solid rgba(0, 0, 0, 0.08);
                transition: border-color 0.2s;
                position: sticky;
                top: 0;
                z-index: 2;

                &:focus-within {
                    border-color: #1976d2;
                }
            }

            .search-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: rgba(0, 0, 0, 0.38);
                flex-shrink: 0;
            }

            .search-input {
                flex: 1;
                border: none;
                outline: none;
                background: transparent;
                font-size: 13px;
                color: rgba(0, 0, 0, 0.87);
                min-width: 0;

                &::placeholder {
                    color: rgba(0, 0, 0, 0.38);
                }
            }

            .user-count {
                font-size: 11px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.38);
                background: rgba(0, 0, 0, 0.06);
                padding: 1px 7px;
                border-radius: 10px;
                flex-shrink: 0;
            }

            /* User list */
            .user-list {
                overflow-y: auto;
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 3px;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 3px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.3);
                    }
                }
            }

            .user-row {
                border-bottom: 1px solid rgba(0, 0, 0, 0.06);
                cursor: pointer;
                transition: background 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.02);
                }
                &:last-child {
                    border-bottom: none;
                }
                &.expanded {
                    background: rgba(0, 0, 0, 0.02);
                }
            }

            .user-summary {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 8px 4px;
            }

            /* Avatar wrapper — contains avatar + presence dot without clipping */
            .avatar-wrapper {
                position: relative;
                width: 32px;
                height: 32px;
                flex-shrink: 0;

                &.clickable {
                    cursor: pointer;
                    border-radius: 50%;
                    transition:
                        transform 0.15s,
                        box-shadow 0.15s;
                    &:hover {
                        transform: scale(1.12);
                        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
                    }
                }
            }

            /* Avatar */
            .avatar {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: rgba(25, 118, 210, 0.1);
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;

                mat-icon {
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                    color: #1976d2;
                }

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }

            .initials {
                font-size: 12px;
                font-weight: 600;
                color: #fff;
                letter-spacing: 0.5px;
                text-transform: uppercase;
                user-select: none;
                line-height: 1;
            }

            /* Online presence indicator */
            .presence-dot {
                position: absolute;
                bottom: -2px;
                right: -2px;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background: #bdbdbd;
                border: 2.5px solid #fff;
                box-sizing: border-box;
                transition:
                    background 0.2s,
                    box-shadow 0.3s;

                &.online {
                    background: #4caf50;
                    box-shadow:
                        0 0 0 2px rgba(76, 175, 80, 0.25),
                        0 0 6px rgba(76, 175, 80, 0.4);
                    animation: presencePulse 2s ease-in-out infinite;
                }
            }

            @keyframes presencePulse {
                0%,
                100% {
                    box-shadow:
                        0 0 0 2px rgba(76, 175, 80, 0.25),
                        0 0 6px rgba(76, 175, 80, 0.4);
                }
                50% {
                    box-shadow:
                        0 0 0 3px rgba(76, 175, 80, 0.15),
                        0 0 10px rgba(76, 175, 80, 0.3);
                }
            }

            /* Action buttons */
            .user-actions {
                display: flex;
                gap: 2px;
                flex-shrink: 0;
            }

            .action-btn {
                background: none;
                border: none;
                cursor: pointer;
                padding: 4px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: rgba(0, 0, 0, 0.35);
                transition:
                    background 0.15s,
                    color 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.06);
                    color: rgba(0, 0, 0, 0.6);
                }

                mat-icon {
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                }
            }

            .user-identity {
                flex: 1;
                display: flex;
                flex-direction: column;
                min-width: 0;
            }

            .user-name {
                font-size: 13px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .user-email {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.45);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .chevron {
                font-size: 20px;
                width: 20px;
                height: 20px;
                color: rgba(0, 0, 0, 0.3);
                flex-shrink: 0;
                transition: color 0.15s;
            }

            /* Details panel */
            .user-details {
                padding: 4px 8px 10px 46px;
            }

            .detail-section {
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            .detail-header {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 11px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.54);
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 4px;

                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            }

            .chip-list {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
            }

            .chip {
                display: inline-block;
                font-size: 11px;
                padding: 2px 8px;
                border-radius: 10px;
                white-space: nowrap;
                max-width: 180px;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            /* chip colors now set dynamically via [style] bindings from colorsService */

            .empty-assignments {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.38);
                font-style: italic;

                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 24px 8px;
                color: rgba(0, 0, 0, 0.38);

                mat-icon {
                    font-size: 36px;
                    width: 36px;
                    height: 36px;
                    margin-bottom: 8px;
                }

                span {
                    font-size: 13px;
                }
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .search-bar {
                    background: #424242;
                    border-color: rgba(255, 255, 255, 0.1);
                }
                .search-icon {
                    color: rgba(255, 255, 255, 0.38);
                }
                .search-input {
                    color: rgba(255, 255, 255, 0.87);
                    &::placeholder {
                        color: rgba(255, 255, 255, 0.38);
                    }
                }
                .user-count {
                    color: rgba(255, 255, 255, 0.38);
                    background: rgba(255, 255, 255, 0.08);
                }
                .user-row {
                    border-bottom-color: rgba(255, 255, 255, 0.06);
                    &:hover,
                    &.expanded {
                        background: rgba(255, 255, 255, 0.04);
                    }
                }
                .user-name {
                    color: rgba(255, 255, 255, 0.87);
                }
                .user-email {
                    color: rgba(255, 255, 255, 0.45);
                }
                .chevron {
                    color: rgba(255, 255, 255, 0.3);
                }
                .detail-header {
                    color: rgba(255, 255, 255, 0.54);
                }
                /* chip colors now set dynamically via [style] bindings from colorsService */
                .empty-assignments {
                    color: rgba(255, 255, 255, 0.38);
                }
                .empty-state {
                    color: rgba(255, 255, 255, 0.38);
                }
                .presence-dot {
                    border-color: #424242;
                    &.online {
                        box-shadow:
                            0 0 0 2px rgba(76, 175, 80, 0.3),
                            0 0 8px rgba(76, 175, 80, 0.5);
                    }
                }
                .avatar-wrapper.clickable:hover {
                    box-shadow: 0 0 0 3px rgba(100, 181, 246, 0.3);
                }
                .action-btn {
                    color: rgba(255, 255, 255, 0.35);
                    &:hover {
                        background: rgba(255, 255, 255, 0.08);
                        color: rgba(255, 255, 255, 0.7);
                    }
                }
                .user-list {
                    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        &:hover {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }
            }
        `,
    ],
})
export class UserDirectoryWidgetComponent implements OnChanges {
    @Input() data!: OrgWidgetData;
    private afs = inject(AngularFirestore);

    constructor(
        private dialogService: OrgDialogService,
        public colorsService: OrgEntityColorsService
    ) {}

    public allUsers: DirectoryUser[] = [];
    public filteredUsers: DirectoryUser[] = [];
    public selectedKey: string | null = null;
    public searchTerm = '';

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.buildDirectory();
        }
    }

    /** Refresh online status without rebuilding the whole directory */
    private updateOnlineStatus(): void {
        const onlineSet = new Set(this.data?.onlineUserIds || []);
        for (const user of this.allUsers) {
            user.isOnline = onlineSet.has(user.key);
        }
        // Re-sort: online users first, then alphabetical
        this.allUsers.sort((a, b) => {
            if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        this.applyFilter();
    }

    public onSearch(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
        this.applyFilter();
    }

    public toggleUser(key: string): void {
        this.selectedKey = this.selectedKey === key ? null : key;
    }

    public trackByKey(_index: number, user: DirectoryUser): string {
        return user.key;
    }

    /** Open the user details dialog directly with full user data */
    public onViewUser(event: Event, user: DirectoryUser): void {
        event.stopPropagation();
        this.openUserDetails(user);
    }

    /** Open user details dialog when avatar is clicked */
    public onAvatarClick(event: Event, user: DirectoryUser): void {
        event.stopPropagation();
        this.openUserDetails(user);
    }

    private openUserDetails(user: DirectoryUser): void {
        const userEntity = (this.data.users || []).find((u) => u.key === user.key);
        const userData = userEntity
            ? { ...userEntity.data, key: user.key, name: user.name }
            : { key: user.key, name: user.name };
        this.dialogService.openOrgUserDetailsDialog(null as any, userData);
    }

    /** Placeholder — will be implemented later */
    public onEmailUser(event: Event, user: DirectoryUser): void {
        event.stopPropagation();
        console.log('[User Directory] Email user:', user.email || user.name);
    }

    /** Placeholder — will be implemented later */
    public onChatUser(event: Event, user: DirectoryUser): void {
        event.stopPropagation();
        console.log('[User Directory] Chat with user:', user.name);
    }

    // ── Build the directory from raw widget data ──

    private buildDirectory(): void {
        const onlineSet = new Set(this.data?.onlineUserIds || []);
        // Build hat lookup: hatKey → hat name
        const hatNameMap = new Map<string, string>();
        for (const hat of this.data.hats || []) {
            if (!hat?.data) continue;
            const name = (hat.data['name'] as string) || hat.key;
            hatNameMap.set(hat.key, name);
        }

        // Build hat → positions lookup
        const hatPositionMap = new Map<string, string[]>();
        for (const hat of this.data.hats || []) {
            if (!hat?.data) continue;
            const positions: string[] = [];
            // positions array on hat
            const posArr = hat.data['positions'] as Array<Record<string, unknown>> | undefined;
            if (Array.isArray(posArr)) {
                for (const pos of posArr) {
                    const posName = (pos['name'] || pos['mainTitle'] || pos['partKey'] || '') as string;
                    if (posName) positions.push(posName);
                }
            }
            // orgPosition single position
            const orgPos = hat.data['orgPosition'] as Record<string, unknown> | undefined;
            if (orgPos) {
                const posName = (orgPos['name'] || orgPos['mainTitle'] || '') as string;
                if (posName && !positions.includes(posName)) positions.push(posName);
            }
            hatPositionMap.set(hat.key, positions);
        }

        // Build hat → security groups lookup
        const hatSgMap = new Map<string, string[]>();
        for (const hat of this.data.hats || []) {
            if (!hat?.data) continue;
            const sgNames: string[] = [];
            const sgArr = hat.data['securityGroups'] as Array<Record<string, unknown>> | undefined;
            if (Array.isArray(sgArr)) {
                for (const sg of sgArr) {
                    const name = (sg['name'] || sg['key'] || '') as string;
                    if (name) sgNames.push(name);
                }
            }
            const sgArr2 = hat.data['securityGroupsArray'] as Array<Record<string, unknown>> | undefined;
            if (Array.isArray(sgArr2)) {
                for (const sg of sgArr2) {
                    const name = (sg['name'] || sg['key'] || '') as string;
                    if (name && !sgNames.includes(name)) sgNames.push(name);
                }
            }
            hatSgMap.set(hat.key, sgNames);
        }

        // Build feature name lookup
        const featureNameMap = new Map<string, string>();
        for (const feat of this.data.features || []) {
            if (!feat?.data) continue;
            featureNameMap.set(feat.key, (feat.data['name'] as string) || feat.key);
        }

        // Process users
        this.allUsers = (this.data.users || [])
            .filter((u: OrgWidgetEntity) => u?.data != null)
            .map((u: OrgWidgetEntity) => {
                const d = u.data;
                const userName = (d['name'] || d['userName'] || d['email'] || u.key) as string;
                const email = (d['email'] || '') as string;
                const photo = (d['photoThumbnail'] || '') as string;

                // Hats
                const userHatKeys = (d['hats'] as string[]) || [];
                const hatNames = userHatKeys.map((hk: string) => hatNameMap.get(hk) || hk).filter((n: string) => !!n);

                // Positions — collected from all user's hats (deduplicated)
                const positionSet = new Set<string>();
                for (const hk of userHatKeys) {
                    const positions = hatPositionMap.get(hk) || [];
                    positions.forEach((p: string) => positionSet.add(p));
                }

                // Security groups — from hats + direct user-level hatsDetails
                const sgSet = new Set<string>();
                for (const hk of userHatKeys) {
                    const sgNames = hatSgMap.get(hk) || [];
                    sgNames.forEach((s: string) => sgSet.add(s));
                }
                // Direct security groups from hatsDetails on user
                const hatsDetails = d['hatsDetails'] as Record<string, Record<string, unknown>> | undefined;
                if (hatsDetails) {
                    for (const sg of Object.values(hatsDetails)) {
                        const name = (sg['name'] || '') as string;
                        if (name) sgSet.add(name);
                    }
                }

                // Features — from user's features array
                const userFeatKeys = (d['features'] as string[]) || [];
                const featureNames = userFeatKeys
                    .map((fk: string) => featureNameMap.get(fk) || fk)
                    .filter((n: string) => !!n);

                return {
                    key: u.key,
                    name: userName,
                    email,
                    photoThumbnail: photo,
                    initials: this.getInitials(userName),
                    avatarColor: (d['profileColor'] as string) || '#9e9e9e',
                    isOnline: onlineSet.has(u.key),
                    hats: hatNames,
                    positions: Array.from(positionSet),
                    securityGroups: Array.from(sgSet),
                    features: featureNames,
                };
            });

        // Sort: online first, then alphabetical
        this.allUsers.sort((a, b) => {
            if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        this.applyFilter();

        // Fetch profile colors from users collection
        this.allUsers.forEach((user) => {
            this.afs
                .doc(`users/${user.key}`)
                .get()
                .pipe(take(1))
                .subscribe((doc: any) => {
                    const data = doc.data();
                    if (data?.profileColor) {
                        user.avatarColor = data.profileColor;
                    }
                });
        });
    }

    private applyFilter(): void {
        const term = this.searchTerm.toLowerCase().trim();
        if (!term) {
            this.filteredUsers = this.allUsers;
            return;
        }
        this.filteredUsers = this.allUsers.filter(
            (u) =>
                u.name.toLowerCase().includes(term) ||
                u.email.toLowerCase().includes(term) ||
                u.hats.some((h) => h.toLowerCase().includes(term)) ||
                u.positions.some((p) => p.toLowerCase().includes(term)) ||
                u.securityGroups.some((sg) => sg.toLowerCase().includes(term))
        );
    }

    /** Extract up to 2 initials from a display name */
    private getInitials(name: string): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return (parts[0]?.[0] || '?').toUpperCase();
    }

    /** Deterministic color from name — same name always gets the same color */
    private getAvatarColor(name: string): string {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    }
}
